import React, {useEffect, useRef, useState} from 'react';
import styles from './styles.module.scss';
import {useTranslation} from "react-i18next";
import useChatWithDana from "@/app/features/home/components/chat_with_dana/slice";
import {BodyTitleText, CalloutText, HeadlineTitleText} from "@/app/components/texts";
import SearchInput from "@/app/components/search_input";
import Space, {spaceSizes} from "@/app/components/Space";
import {motion, AnimatePresence} from "framer-motion";
import Image from "@/app/components/image";
import {Assets} from "@/app/utils/assets";
import {s} from "@/app/utils/s";
import classNames from "classnames";
import IconButton from "@/app/components/icon_button";
import LazyImage from "@/app/components/lazy_image";
import NewCustomCssTransition from "@/app/components/new_custom_css_transition/widget";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChatInput from "@/app/components/chat_input";
import MessageBlock from "@/app/features/home/components/chat_with_dana/message_block";

const ChatWithDana: React.FC = () => {
  const {t} = useTranslation();
  const {
    state,
    init,
    setCurrentChat,
    toggleMenu,
    getChat,
    toggleShowDelete,
    deleteChat,
    sendMessage,
    createNewChat,
    addFiles
  } = useChatWithDana();
  const contentRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [render, setRender] = useState(false);

  const handleScroll = () => {
    if (!contentRef.current) return;
    const {scrollTop, clientHeight, scrollHeight} = contentRef.current;
    const threshold = 10;
    if (Math.abs(scrollTop) >= scrollHeight - clientHeight - threshold) {
      getChat();
    }
  };

  const handleTransitionEnd = () => {
    if (!state.showDeleteButton) {
      setRender(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (state.showDeleteButton) {
      setRender(true);
    }
  }, [state.showDeleteButton]);

  useEffect(() => {
    const current = contentRef.current;
    if (current) {
      current.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (current) {
        current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [getChat]);

  return (
    <div className={styles.wrapper}>
      <div className={classNames(styles.chats, {[styles.hide]: !state.showMenu})}>
        <Space h={spaceSizes.s28}/>
        <SearchInput/>
        <Space h={spaceSizes.s28}/>
        <div className={styles.chatsInner}>
          {state.chats.map(chat => (
            <div key={chat.id} onClick={() => setCurrentChat(chat.id)} className={styles.lastMessageWrapper}>
              <BodyTitleText text={chat.lastUserRequest}
                             className={classNames(styles.lastMessage, {[styles.selected]: state.currentChat === chat.id})}/>
            </div>
          ))}
        </div>
      </div>
      <div className={classNames(styles.chat, {[styles.expanded]: !state.showMenu})}>
        <div className={styles.header}>
          <IconButton asset={Assets.menu} onClick={() => toggleMenu()}/>
          <Space w={spaceSizes.s20}/>
          <HeadlineTitleText text={t(s.danaAssist)} className={styles.title}/>
          <Space w={spaceSizes.infinity}/>
          <IconButton asset={Assets.createNew} onClick={() => createNewChat()}/>
          <Space w={spaceSizes.s20}/>
          <IconButton asset={Assets.menuDots} onClick={() => toggleShowDelete()}/>
          <NewCustomCssTransition bool={render} children={<div
            className={classNames(styles.deleteButton, {[styles.show]: state.showDeleteButton})}
            onTransitionEnd={handleTransitionEnd}
            onClick={() => deleteChat()}
          >
            <CalloutText text={t(s.deleteNote)} className={styles.text}/>
            <Space w={spaceSizes.s50}/>
            <Image asset={Assets.trashRed}/>
          </div>}/>
        </div>
        {!state.currentChat && (
          <div className={styles.emptyState}>
            <LazyImage className={styles.liquid} asset={Assets.liquidGif}/>
            <HeadlineTitleText text={t(s.doYouHaveAQuestionForMe)}/>
            <Space h={spaceSizes.s50}/>
            <div className={styles.buttons}>
              {[
                {
                  asset: Assets.image, onClick: () => {
                  }, text: t(s.diagnosticImaging)
                },
                {
                  asset: Assets.calculators, onClick: () => {
                  }, text: t(s.medicalCalculators)
                },
                {
                  asset: Assets.information, onClick: () => {
                  }, text: t(s.treatmentFormulation)
                },
                {
                  asset: Assets.differentialDiagnostic, onClick: () => {
                  }, text: t(s.differentialCaseDiagnosis)
                },
                {
                  asset: Assets.laboratory, onClick: () => {
                  }, text: t(s.laboratoryResultsInterpretation)
                }
              ].map((b, index) => (
                <div key={index} className={styles.button} onClick={b.onClick}>
                  <Image asset={b.asset}/>
                  <Space h={spaceSizes.s8}/>
                  <BodyTitleText text={b.text}/>
                </div>
              ))}
            </div>
          </div>
        )}
        {state.currentChat && (
          <div className={styles.content} ref={contentRef}>
            {state.chat.map((m, index) => {
              const isFirst = index === 0;
              return (
                <MessageBlock userRequest={m.userRequest}
                              modelResponse={m.modelResponse} fileLinks={m.filesLinks} isFirst={isFirst} index={index}/>
              );
            })}
          </div>
        )}
        <ChatInput
          textareaRef={textareaRef}
          send={(ref) => sendMessage(ref)}
          onFilesSelected={(files) => {
            addFiles(files);
          }}
          hasSending={state.hasSending}
          filesCount={state.files.length}
        />
        {/*<div className={styles.footerWrapper}>*/}
        {/*  <div className={styles.inputWrapper}>*/}
        {/*    <IconButton asset={Assets.paperclip}/>*/}
        {/*    <Space w={spaceSizes.s16}/>*/}
        {/*    <textarea*/}
        {/*      ref={textareaRef}*/}
        {/*      className={styles.input}*/}
        {/*      rows={1}*/}
        {/*      onInput={handleInput}*/}
        {/*    />*/}
        {/*    <Space w={spaceSizes.s16}/>*/}
        {/*    <IconButton asset={Assets.smallMicrophone}/>*/}
        {/*  </div>*/}
        {/*  <Space w={spaceSizes.s20}/>*/}
        {/*  <div className={styles.sendButton} onClick={() => send(textareaRef)}>*/}
        {/*    <Image asset={Assets.send}/>*/}
        {/*  </div>*/}
        {/*</div>*/}
      </div>
    </div>
  );
};

export default ChatWithDana;
